import axios from 'axios'
import { Course, CourseDto } from '../types/course'
import { getAccessToken } from '../auth/tokenStore'

// Default to a relative base so calls go through the /api proxy:
// vite dev proxy -> localhost:5010, and nginx -> golfapp-api:8080 in Docker.
const API_URL = import.meta.env.VITE_API_URL || ''

export interface GPSCoordinateDto {
  holeId: string
  latitude: number
  longitude: number
  elevation?: number
  type: 'tee' | 'green' | 'hazard'
  teeType?: string
}

export interface CourseDraftHole { number: number; par: number | null; handicap: number | null; distances: Record<string, number | null>; parSets?: Record<string, number | null>; handicapSets?: Record<string, number | null>; distanceSets?: Record<string, Record<string, number | null>> }
export interface CourseDraftPayload { courseName: string; location: string; distanceUnit: 'yards' | 'metres'; holes: CourseDraftHole[] }
export interface ExtractionResult { payload: CourseDraftPayload; warnings: string[]; errors: string[] }
export interface ScorecardReviewRow { id: string; labelEvidence: string; cells: (number|null)[]; confidence: number }
export interface DocumentExtractionResult extends ExtractionResult { method: string; reviewStatus: string; confidence: number; previewText: string; pagesProcessed: number; uncertainFields: string[]; reviewRows?: ScorecardReviewRow[] }
export interface VisionJob { jobId: string; status: 'queued'|'running'|'completed'|'failed'|'cancelled'; createdAt: string; updatedAt: string; result?: DocumentExtractionResult; error?: string }
export interface VisionJobCreated { jobId: string; jobKey: string; status: string }
export interface GuidedGridCalibration { rotation: 0|90|180|270; panels: { startHole: 1|10; corners: { x: number; y: number }[]; teeRows: string[] }[] }
export interface ContributionResponse { seriesId: string; revision: number; status: string; sourceType: string; sourceName: string; sourceNotes?: string; payload: CourseDraftPayload; confirmedCourseId?: string; updatedAt: string; editKey?: string }
export interface GeoPoint { latitude: number; longitude: number }
export interface GolfHoleGeometry { number: number; par?: number; holeId: string; path: GeoPoint[]; greenBoundary: GeoPoint[]; greenCentre: GeoPoint; approachPoint: GeoPoint; holeElement: {type:string;id:number;version:number}; greenElement: {type:string;id:number;version:number}; matchDistanceMetres:number; confidence:string }
export interface GolfHazardGeometry { key:string; kind:'bunker'|'water'; boundary:GeoPoint[]; sourceElement:{type:string;id:number;version:number}; nearbyHoleNumbers:number[]; confidence:string }
export interface GolfGeometryPreview { provider:string; licence:string; attribution:string; sourceUrl:string; retrievedAt:string; fingerprint:string; holes:GolfHoleGeometry[]; warnings:string[]; unmatchedGreenCount:number; genericTeeFeatureCount:number; hazards?:GolfHazardGeometry[] }
export interface CourseGeometrySource { provider:string; licence:string; attribution:string; sourceUrl:string; retrievedAt:string; fingerprint:string; geometryJson:string }
export interface RoundEventInput { eventId:string; deviceId:string; type:string; occurredAt:string; payload:Record<string,unknown> }
export interface RoundSessionResponse { id:string; courseId:string; teeName:string; isDemo?:boolean; schemaVersion:number; version:number; status:string; confirmedGeometryAvailable:boolean; geometryRetrievedAt?:string; events:{eventId:string;deviceId:string;type:string;payloadJson:string;occurredAt:string;receivedAt:string}[] }
export interface RoundSessionSummary { id:string; courseId:string; courseName:string; teeName:string; isDemo?:boolean; version:number; status:string; holesWithData:number; eventCount:number; createdAt:string; updatedAt:string }
export interface SyncEventsResponse { version:number; rebased:boolean; acknowledgedEventIds:string[] }

const api = axios.create({
  baseURL: API_URL,
  // No global Content-Type: axios sets 'application/json' for object bodies and
  // 'multipart/form-data' (with the required boundary) for FormData on its own.
  // A hard-coded JSON default leaks onto file uploads → 400 "No files provided".
})

export const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError(error)) return fallback
  const data = error.response?.data
  if (typeof data === 'string') return data
  if (data && typeof data === 'object' && 'title' in data && typeof data.title === 'string') return data.title
  return fallback
}

// Attach the LeTech ID access token (if signed in) to every request so the
// backend can authorize it. Unauthenticated requests still go through while
// endpoints remain open.
api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export const courseService = {
  async getAllCourses(): Promise<Course[]> {
    const response = await api.get<Course[]>('/api/courses')
    return response.data
  },

  async getCourse(id: string): Promise<Course> {
    const response = await api.get<Course>(`/api/courses/${id}`)
    return response.data
  },

  async createCourse(course: CourseDto): Promise<Course> {
    const response = await api.post<Course>('/api/courses', course)
    return response.data
  },

  async updateCourse(id: string, course: CourseDto): Promise<Course> {
    const response = await api.put<Course>(`/api/courses/${id}`, course)
    return response.data
  },

  async updateCourseCoordinates(id: string, latitude: number, longitude: number): Promise<void> {
    await api.patch(`/api/courses/${id}/coordinates`, { latitude, longitude })
  },

  async deleteCourse(id: string): Promise<void> {
    await api.delete(`/api/courses/${id}`)
  },
}

export const roundSyncService = {
  async list():Promise<RoundSessionSummary[]>{ return (await api.get('/api/v1/round-sessions')).data },
  async start(sessionId:string, courseId:string, teeName:string, isDemo=false):Promise<RoundSessionResponse>{
    return (await api.post('/api/v1/round-sessions',{sessionId,courseId,teeName,isDemo})).data
  },
  async get(sessionId:string):Promise<RoundSessionResponse>{
    return (await api.get(`/api/v1/round-sessions/${sessionId}`)).data
  },
  async sync(sessionId:string, expectedVersion:number, events:RoundEventInput[]):Promise<SyncEventsResponse>{
    return (await api.post(`/api/v1/round-sessions/${sessionId}/events`,{expectedVersion,events})).data
  },
}

export const gpsService = {
  async getGPSCoordinates(courseId: string): Promise<GPSCoordinateDto[]> {
    const response = await api.get<GPSCoordinateDto[]>(`/api/courses/${courseId}/gps-coordinates`)
    return response.data
  },

  async saveGPSCoordinates(courseId: string, coordinates: GPSCoordinateDto[]): Promise<void> {
    await api.post(`/api/courses/${courseId}/gps-coordinates/bulk`, coordinates)
  },

  async saveCourseMap(courseId: string, coordinates: GPSCoordinateDto[], clubhouse: { lat: number; lng: number } | null): Promise<void> {
    await api.put(`/api/courses/${courseId}/gps-coordinates/map`, {
      coordinates,
      clubhouse: clubhouse ? { latitude: clubhouse.lat, longitude: clubhouse.lng } : null,
    })
  },
}

export const geometryService = {
  async preview(courseId:string, signal?:AbortSignal): Promise<GolfGeometryPreview> {
    return (await api.post<GolfGeometryPreview>(`/api/courses/${courseId}/geometry/osm-preview`, {}, {signal})).data
  },
  async confirm(courseId:string, preview:GolfGeometryPreview, confirmedHoleNumbers:number[], confirmedPlayingLineHoleNumbers:number[]=[], confirmedHazardKeys:string[]=[]) {
    const endpoint=preview.provider==='UserSuppliedGeoJSON'?'manual-confirm':'osm-confirm'
    return (await api.put(`/api/courses/${courseId}/geometry/${endpoint}`, {preview, confirmedHoleNumbers, confirmedPlayingLineHoleNumbers, confirmedHazardKeys})).data
  },
  async previewManual(courseId:string, geoJson:string, sourceName:string, licence:string):Promise<GolfGeometryPreview>{
    return (await api.post<GolfGeometryPreview>(`/api/courses/${courseId}/geometry/manual-preview`,{geoJson,sourceName,licence})).data
  },
  async get(courseId:string):Promise<CourseGeometrySource> { return (await api.get<CourseGeometrySource>(`/api/courses/${courseId}/geometry`)).data },
}

export const contributionService = {
  async extract(text: string, distanceUnit: 'yards' | 'metres'): Promise<ExtractionResult> {
    return (await api.post<ExtractionResult>('/api/course-contributions/extract', { text, distanceUnit })).data
  },
  async extractDocument(file: File, distanceUnit: 'yards' | 'metres', signal?: AbortSignal): Promise<DocumentExtractionResult> {
    const data = new FormData(); data.append('file', file); data.append('distanceUnit', distanceUnit)
    return (await api.post<DocumentExtractionResult>('/api/course-contributions/extract-document', data, { signal })).data
  },
  async extractGuidedGrid(file: File, distanceUnit: 'yards' | 'metres', calibration: GuidedGridCalibration, signal?: AbortSignal): Promise<DocumentExtractionResult> {
    const data = new FormData(); data.append('file', file); data.append('distanceUnit', distanceUnit); data.append('calibration', JSON.stringify(calibration))
    return (await api.post<DocumentExtractionResult>('/api/course-contributions/extract-guided-grid', data, { signal })).data
  },
  async visionStatus(): Promise<{ available: boolean; engine: string }> {
    return (await api.get('/api/course-contributions/vision-status')).data
  },
  async startVisionJob(file: File, distanceUnit: 'yards'|'metres', calibration?: GuidedGridCalibration): Promise<VisionJobCreated> {
    const data = new FormData(); data.append('file', file); data.append('distanceUnit', distanceUnit)
    if (calibration) data.append('calibration', JSON.stringify(calibration))
    return (await api.post<VisionJobCreated>('/api/course-contributions/vision-jobs', data)).data
  },
  async getVisionJob(jobId: string, jobKey: string): Promise<VisionJob> {
    return (await api.get<VisionJob>(`/api/course-contributions/vision-jobs/${jobId}`, { headers: { 'X-Extraction-Key': jobKey } })).data
  },
  async cancelVisionJob(jobId: string, jobKey: string): Promise<void> {
    await api.delete(`/api/course-contributions/vision-jobs/${jobId}`, { headers: { 'X-Extraction-Key': jobKey } })
  },
  async create(sourceType: string, sourceName: string, sourceNotes: string, payload: CourseDraftPayload): Promise<ContributionResponse> {
    return (await api.post<ContributionResponse>('/api/course-contributions', { sourceType, sourceName, sourceNotes, payload })).data
  },
  async get(seriesId: string, editKey: string): Promise<ContributionResponse> {
    return (await api.get<ContributionResponse>(`/api/course-contributions/${seriesId}`, { headers: { 'X-Contribution-Key': editKey } })).data
  },
  async correct(seriesId: string, editKey: string, sourceType: string, sourceName: string, sourceNotes: string, payload: CourseDraftPayload): Promise<ContributionResponse> {
    return (await api.put<ContributionResponse>(`/api/course-contributions/${seriesId}`, { sourceType, sourceName, sourceNotes, payload }, { headers: { 'X-Contribution-Key': editKey } })).data
  },
  async confirm(seriesId: string, revision: number, editKey: string): Promise<Course> {
    return (await api.post<Course>(`/api/course-contributions/${seriesId}/confirm`, { revision }, { headers: { 'X-Contribution-Key': editKey } })).data
  },
}

export default api
